// Agent Function admin client (AI Functions tab).
//
// Built against the real AIFunction backend, confirmed by reading the controllers directly:
//   BaseAgentFunctionController  api/v1/ai-function/agent-functions
//   BaseFunctionTypeController   api/v1/ai-function/function-types
// Wire-shape rules follow teamAdminApiClient.ts. Create/Update nest entity fields under `data`.
// Search and action requests are root-level. The entity has no IsActive flag, so there are no
// activate/deactivate calls here.

import { BaseAdminApiClient, MAX_PAGE_SIZE, type AdminApiConfig, type AdminApiResult } from './baseAdminApiClient';
import type { AgentFunctionDTO, AgentFunctionFormValues, LookupOption } from '../types';

const PAGE_ALL = { PageNumber: 1, PageSize: MAX_PAGE_SIZE };

export class AgentFunctionAdminApiClient extends BaseAdminApiClient {
  constructor(config: AdminApiConfig) {
    super(config);
  }

  async getAllFunctions(): Promise<AdminApiResult<AgentFunctionDTO[]>> {
    const result = await this.postQuery<AgentFunctionDTO[]>('/api/v1/ai-function/agent-functions/list', { PageInfo: PAGE_ALL });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  /** One page of AI functions for the AI Functions list. Server-paged. */
  async getFunctionsPage(page: number, pageSize: number): Promise<AdminApiResult<AgentFunctionDTO[]>> {
    const result = await this.postQuery<AgentFunctionDTO[]>('/api/v1/ai-function/agent-functions/list', { PageInfo: { PageNumber: page, PageSize: pageSize } });
    return { ...result, data: Array.isArray(result.data) ? result.data : [] };
  }

  async getFunctionById(functionId: number): Promise<AdminApiResult<AgentFunctionDTO>> {
    return this.postQuery<AgentFunctionDTO>(`/api/v1/ai-function/agent-functions/${functionId}`, { ID: { ID: functionId } });
  }

  async createFunction(fn: AgentFunctionFormValues): Promise<AdminApiResult<AgentFunctionDTO>> {
    const response = await this.post<{ id?: { id?: number } }>('/api/v1/ai-function/agent-functions', fn);
    const newID = response.data?.id?.id;
    if (response.success && newID) {
      return { ...response, data: { AgentFunctionID: newID, ...fn } as AgentFunctionDTO };
    }
    return response as unknown as AdminApiResult<AgentFunctionDTO>;
  }

  async updateFunction(functionId: number, fn: Partial<AgentFunctionFormValues>): Promise<AdminApiResult<AgentFunctionDTO>> {
    const response = await this.put<unknown>(`/api/v1/ai-function/agent-functions/${functionId}`, {
      AgentFunctionID: functionId,
      ...fn,
    });
    if (response.success) {
      return { ...response, data: { AgentFunctionID: functionId, ...fn } as AgentFunctionDTO };
    }
    return response as unknown as AdminApiResult<AgentFunctionDTO>;
  }

  async deleteFunction(functionId: number): Promise<AdminApiResult<void>> {
    return this.del<void>(`/api/v1/ai-function/agent-functions/${functionId}`, { id: functionId });
  }

  /** Function-type lookup for the editor. FunctionTypeID is the key and TypeName the label. */
  async getFunctionTypes(): Promise<AdminApiResult<LookupOption[]>> {
    const result = await this.postQuery<Array<Record<string, unknown>>>('/api/v1/ai-function/function-types/active', {
      PageInfo: { PageNumber: 1, PageSize: MAX_PAGE_SIZE },
    });
    if (!result.success || !Array.isArray(result.data)) return { ...result, data: [] };
    const options = result.data.map((item) => ({
      id: (item.FunctionTypeID as number) ?? 0,
      name: (item.TypeName as string) ?? String(item.FunctionTypeID ?? ''),
      ...item,
    }));
    return { success: true, data: options };
  }
}
